import { useMemo } from 'react'

import {
  matchFilterBetweenDates, matchFilterNumber, matchFilterString, sortArrayBy,
} from '@domain-group/javascript-utils'

import { useLoadMore, useWatchEntityUpdates } from '@domain-group/react-hooks'

import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import type { CampaignContributionModel, CampaignContributionRequestOptions } from '@models/types'
import type { ModuleState } from '@redux/modules/types'

import formatDate from '@functions/formatDate'

const watchEntityKeys = ['campaignContributions']

type CampaignContributionFilters = {
  agency_id?: number,
  campaign_id?: number,
  end_date?: string,
  paid_by?: 'agency' | 'agent' | 'marketing' | 'vendor',
  payment_method?: string,
  payment_received?: 'true' | 'false',
  start_date?: string,
  string?: string,
}

type UseCampaignContributionsOptions = {
  filters?: CampaignContributionFilters,
  performHttpRequests?: boolean,
  requestOptions?: CampaignContributionRequestOptions,
}

function useCampaignContributions(options: UseCampaignContributionsOptions) {
  const { filters = {}, requestOptions } = options

  const {
    agency_id: filterAgencyId,
    campaign_id: filterCampaignId,
    end_date: filterCreatedBefore,
    paid_by: filterPaidBy,
    payment_method: filterPaymentMethod,
    payment_received: filterPaymentReceived,
    start_date: filterCreatedAfter,
    string: filterString,
  } = filters

  const {
    updatedEntities: { campaignContributions: campaignContributionsUpdatedAt },
  } = useWatchEntityUpdates(watchEntityKeys)

  const { campaigns, campaignContributions } = useSelector(reduxState => reduxState.entities)

  const filteredCampaignContributions = useMemo(() => {
    const filtered = Object.values(campaignContributions).filter((campaignContribution: CampaignContributionModel) => {
      const {
        campaign_id, dates, id, paid_by, payment_method, payment_received, reference,
      } = campaignContribution

      const createdAt = formatDate(dates?.created?.date_time, 'dd/MM/yyyy HH:mm')

      // @ts-expect-error TS2538
      const { agency_id } = campaigns[campaign_id] || {}

      const matchId = id.toString() === filterString
      const matchReference = matchFilterString(reference, filterString)
      const matchString = matchId || matchReference

      const matchCampaignId = matchFilterNumber(campaign_id, filterCampaignId)
      const matchAgency = matchFilterNumber(agency_id, filterAgencyId)
      const matchPaidBy = filterPaidBy ? paid_by === filterPaidBy : true
      const matchPaymentMethod = filterPaymentMethod ? payment_method === filterPaymentMethod : true
      const matchPaymentReceived = filterPaymentReceived ? payment_received?.toString() === filterPaymentReceived : true
      const matchCreatedAt = matchFilterBetweenDates(createdAt, filterCreatedAfter, filterCreatedBefore)

      return matchAgency
      && matchCampaignId
      && matchCreatedAt
      && matchPaidBy
      && matchPaymentMethod
      && matchPaymentReceived
      && matchString
    })

    return sortArrayBy(filtered, 'desc', 'id')
  }, [campaignContributionsUpdatedAt, JSON.stringify(filters)])

  const filteredCampaignContributionsCount = filteredCampaignContributions.length
  const hasFilteredCampaignContributions = !!filteredCampaignContributionsCount
  // @ts-expect-error TS18048
  const contributionTotal = filteredCampaignContributions.reduce((acc, item) => acc + item.amount, 0)
  const contributionsReceived = filteredCampaignContributions.reduce(
    // @ts-expect-error TS18048
    (acc, item) => (item.payment_received ? acc + item.amount : acc),
    0,
  )

  const loadMorePayload = useLoadMore({
    filters,
    loadedCount: filteredCampaignContributionsCount,
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
    performHttpRequests,
  } = loadMorePayload

  const { loading: loadingCampaignContributions } = useReduxAction(
    'campaignContributions',
    'loadCampaignContributions',
    {
      ...requestOptions,
      ...filtersWithOffset,
      limit,
    },
    [filtersWithOffset, performHttpRequests],
    {
      shouldPerformFn: ({ loading }: ModuleState) => performHttpRequests && !loading,
    },
  )

  return {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    contributionsReceived,
    contributionTotal,
    filteredCampaignContributions,
    filteredCampaignContributionsCount,
    hasFilteredCampaignContributions,
    loading: loadingCampaignContributions,
  }
}

export default useCampaignContributions
